import React, { useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import "../Table/table.css";
import { TbFileTypeXls } from "react-icons/tb";

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 768;
}

function renderCellValue(row, column) {
  if (column.render) return column.render(row);
  return row[column.key];
}

function wrapNoTranslate(value) {
  if (React.isValidElement(value)) return value;

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return (
      <span className="notranslate" translate="no">
        {String(value)}
      </span>
    );
  }

  if (value == null) {
    return (
      <span className="notranslate" translate="no">
        -
      </span>
    );
  }

  return value;
}

export default function AttendanceTable({
  columns = [],
  desktopColumns,
  rows = [],
  loading = false,
  emptyText = "Sin registros",
  actions,
  onExport,
  toolbarLeft,
  toolbarRight,
  getRowKey,
  dense = false,
  footerText = "  ",
  mobileCardView = true,
  pagination = true,
  pageSize = 10,
  pageSizeOptions,
  onPageSizeChange,
  onVisibleRowsChange,
  expandable,
  mobileExpandableAfterKey,
}) {
  const keyGetter = getRowKey || ((row, i) => row.id ?? i);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(() => isMobileViewport());
  const [expandedMobileKeys, setExpandedMobileKeys] = useState([]);

  useEffect(() => {
    const onResize = () => setIsMobile(isMobileViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const totalRows = rows.length;
  const totalPages = pagination ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1;

  useEffect(() => {
    setCurrentPage(1);
  }, [rows, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const mobileRows = useMemo(() => {
    if (!pagination) return rows;
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, pagination, currentPage, pageSize]);

  useEffect(() => {
    onVisibleRowsChange?.(mobileRows);
  }, [mobileRows, onVisibleRowsChange]);

  const antdColumns = useMemo(() => {
    const sourceColumns = desktopColumns || columns;
    const mapped = sourceColumns.map((column) => ({
      key: column.key,
      title: column.header,
      dataIndex: column.key,
      width: column.width,
      align: column.align || "left",
      ellipsis: Boolean(column.ellipsis),
      render: (_, row) => {
        const rawValue = renderCellValue(row, column);
        const cellTitle =
          column.ellipsis && !React.isValidElement(rawValue) && rawValue != null
            ? String(rawValue)
            : undefined;

        return (
          <div
            className={`att-cell ${column.ellipsis ? "att-cell--ellipsis" : ""}`.trim()}
            title={cellTitle}
            style={{ textAlign: column.align || "left" }}
            translate="no"
          >
            {wrapNoTranslate(rawValue)}
          </div>
        );
      },
    }));

    if (actions) {
      mapped.push({
        key: "__actions__",
        title: "Acciones",
        width: 96,
        align: "right",
        render: (_, row) => <div className="actions">{actions(row)}</div>,
      });
    }

    return mapped;
  }, [actions, columns, desktopColumns]);

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const startRecord = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRows);
  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const out = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    return Array.from(out)
      .filter((n) => n >= 1 && n <= totalPages)
      .sort((a, b) => a - b);
  }, [currentPage, totalPages]);

  const showMobileCards = mobileCardView && isMobile;
  const renderMobileDetails = (row, idx) => {
    if (expandable?.rowExpandable?.(row) === false || !expandable?.expandedRowRender) return null;

    const key = keyGetter(row, idx);
    const expanded = expandedMobileKeys.includes(key);
    const toggleExpanded = () => {
      setExpandedMobileKeys((keys) =>
        expanded ? keys.filter((item) => item !== key) : [...keys, key]
      );
      expandable.onExpand?.(!expanded, row);
    };

    return (
      <div className="att-mobile-card__details">
        <button
          type="button"
          className="att-mobile-card__details-toggle"
          aria-expanded={expanded}
          onClick={toggleExpanded}
        >
          {expanded ? "Ocultar horarios completos" : "Ver horarios completos"}
        </button>
        {expanded && expandable.expandedRowRender(row)}
      </div>
    );
  };
  const desktopPagination = pagination
    ? {
        current: currentPage,
        pageSize,
        total: totalRows,
        showSizeChanger: onPageSizeChange
          ? { popupClassName: "att-page-size-popup", showSearch: false }
          : false,
        pageSizeOptions,
        showQuickJumper: false,
        onChange: (page, nextPageSize) => {
          setCurrentPage(page);
          if (nextPageSize !== pageSize) onPageSizeChange?.(nextPageSize);
        },
      }
    : false;

  return (
    <div className="att-card notranslate" translate="no">
      {(toolbarLeft || toolbarRight || onExport) && (
        <div className="att-toolbar">
          <div className="att-toolbar__left">{toolbarLeft}</div>

          <div className="att-toolbar__right">
            {toolbarRight}

            {onExport && (
              <button type="button" className="btn primary" onClick={onExport}>
                <TbFileTypeXls className="btn-icon" />
                <span>Generar reporte</span>
              </button>
            )}
          </div>
        </div>
      )}

      {!showMobileCards && (
        <div className="att-table-wrap att-table-wrap--antd">
          <Table
            className={`att-antd-table ${dense ? "table--dense" : ""}`.trim()}
            columns={antdColumns}
            dataSource={rows}
            rowKey={keyGetter}
            loading={loading ? { spinning: true, tip: "Cargando registros..." } : false}
            pagination={desktopPagination}
            tableLayout="fixed"
            size={dense ? "small" : "middle"}
            expandable={expandable}
            locale={{ emptyText }}
          />

          {footerText && <div className="att-footer-text">{footerText}</div>}
        </div>
      )}

      {showMobileCards && (
        <div className="att-mobile-list">
          {loading && (
            <div className="att-mobile-state">
              <div className="att-loading">
                <div className="att-loading__spinner" />
                <span>Cargando registros...</span>
              </div>
            </div>
          )}

          {!loading && totalRows === 0 && (
            <div className="att-mobile-state empty">{emptyText}</div>
          )}

          {!loading &&
            mobileRows.map((row, idx) => (
              <div
                className="att-mobile-card notranslate"
                translate="no"
                key={keyGetter(row, idx)}
              >
                <div className="att-mobile-card__body">
                  {columns.filter((column) => !column.mobileHidden).map((column) => {
                    const value = renderCellValue(row, column);
                    return (
                      <React.Fragment key={column.key}>
                        <div className="att-mobile-item">
                          <div className="att-mobile-item__label">{column.header}</div>
                          <div
                            className={`att-mobile-item__value ${
                              column.ellipsis ? "ellipsis" : ""
                            }`}
                            title={
                              column.ellipsis && !React.isValidElement(value) && value != null
                                ? String(value)
                                : undefined
                            }
                          >
                            {wrapNoTranslate(value)}
                          </div>
                        </div>
                        {column.key === mobileExpandableAfterKey && renderMobileDetails(row, idx)}
                      </React.Fragment>
                    );
                  })}
                </div>

                {actions && <div className="att-mobile-card__actions">{actions(row)}</div>}
              </div>
            ))}

          {pagination && !loading && totalRows > 0 && (
            <div className="att-pagination-simple att-pagination-mobile">
              <button
                type="button"
                className="att-page-btn-simple"
                onClick={handlePrev}
                disabled={currentPage === 1}
              >
                Anterior
              </button>

              <div className="att-page-numbers">
                {pageNumbers.map((p, idx) => {
                  const prev = pageNumbers[idx - 1];
                  const showGap = typeof prev === "number" && p - prev > 1;
                  return (
                    <React.Fragment key={p}>
                      {showGap && <span className="att-page-gap">…</span>}
                      <button
                        type="button"
                        className={`att-page-num ${p === currentPage ? "active" : ""}`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>

              <button
                type="button"
                className="att-page-btn-simple"
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                Siguiente
              </button>
            </div>
          )}

          {!loading && (
            <div className="att-dt-info">
              Registros del ({startRecord} al {endRecord}) total de {totalRows} registros
            </div>
          )}

          {footerText && <div className="att-footer-text">{footerText}</div>}
        </div>
      )}
    </div>
  );
}
